import { useState, useEffect } from 'react';
import { supabase } from '../../../lib/supabase';

interface AttorneyDoc {
  id: string;
  client_id: string;
  file_name: string;
  file_path: string;
  file_size: number;
  file_type: string;
  description: string | null;
  is_viewed: boolean;
  viewed_at: string | null;
  uploaded_at: string;
}

interface PreviewState {
  doc: AttorneyDoc;
  url: string;
}

interface AttorneyDocumentsProps {
  clientId: string;
  onUnreadCountChange?: (count: number) => void;
}

function formatFileSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function getFileIcon(type: string): string {
  if (type === 'application/pdf') return 'ri-file-pdf-line text-red-400';
  if (type.startsWith('image/')) return 'ri-image-line text-green-400';
  if (type.includes('word')) return 'ri-file-word-line text-[#FDB913]';
  if (type.includes('excel') || type.includes('spreadsheet')) return 'ri-file-excel-line text-green-500';
  return 'ri-file-text-line text-white/60';
}

function isPreviewable(type: string): boolean {
  return type === 'application/pdf' || type.startsWith('image/');
}

export default function AttorneyDocuments({ clientId, onUnreadCountChange }: AttorneyDocumentsProps) {
  const [docs, setDocs] = useState<AttorneyDoc[]>([]);
  const [loading, setLoading] = useState(true);
  const [downloadingId, setDownloadingId] = useState<string | null>(null);
  const [previewLoading, setPreviewLoading] = useState<string | null>(null);
  const [preview, setPreview] = useState<PreviewState | null>(null);

  useEffect(() => {
    if (clientId) loadDocs();
  }, [clientId]);

  const loadDocs = async () => {
    setLoading(true);
    try {
      const { data, error } = await supabase
        .from('attorney_documents')
        .select('*')
        .eq('client_id', clientId)
        .order('uploaded_at', { ascending: false });

      if (error) throw error;
      const docList = (data as AttorneyDoc[]) || [];
      setDocs(docList);

      const unread = docList.filter(d => !d.is_viewed).length;
      onUnreadCountChange?.(unread);
    } catch (err) {
      console.error('Load attorney docs error:', err);
    } finally {
      setLoading(false);
    }
  };

  const markViewed = async (doc: AttorneyDoc) => {
    if (doc.is_viewed) return;
    try {
      await supabase
        .from('attorney_documents')
        .update({ is_viewed: true, viewed_at: new Date().toISOString() })
        .eq('id', doc.id);
      setDocs(prev => prev.map(d => d.id === doc.id ? { ...d, is_viewed: true, viewed_at: new Date().toISOString() } : d));
      const newUnread = docs.filter(d => !d.is_viewed && d.id !== doc.id).length;
      onUnreadCountChange?.(newUnread);
    } catch (err) {
      console.error('Mark viewed error:', err);
    }
  };

  const handleDownload = async (doc: AttorneyDoc) => {
    setDownloadingId(doc.id);
    await markViewed(doc);
    try {
      const { data, error } = await supabase.storage.from('case-documents').download(doc.file_path);
      if (error) throw error;
      const url = URL.createObjectURL(data);
      const a = document.createElement('a');
      a.href = url;
      a.download = doc.file_name;
      a.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Download failed:', err);
    } finally {
      setDownloadingId(null);
    }
  };

  const handlePreview = async (doc: AttorneyDoc) => {
    setPreviewLoading(doc.id);
    await markViewed(doc);
    try {
      const { data, error } = await supabase.storage
        .from('case-documents')
        .createSignedUrl(doc.file_path, 3600);
      if (error) throw error;
      if (data?.signedUrl) setPreview({ doc, url: data.signedUrl });
    } catch (err) {
      console.error('Preview failed:', err);
    } finally {
      setPreviewLoading(null);
    }
  };

  const unreadCount = docs.filter(d => !d.is_viewed).length;

  if (loading) {
    return (
      <div className="bg-white/5 backdrop-blur-md border border-white/10 rounded-2xl p-8 text-center">
        <i className="ri-loader-4-line animate-spin text-[#FDB913] text-2xl"></i>
      </div>
    );
  }

  if (docs.length === 0) return null;

  return (
    <>
      {preview && (
        <div className="fixed inset-0 z-[200] bg-black/90 backdrop-blur-sm flex flex-col">
          <div className="flex items-center justify-between px-6 py-4 bg-[#0d1520] border-b border-white/10 flex-shrink-0">
            <div className="flex items-center gap-3 min-w-0">
              <div className="w-8 h-8 flex items-center justify-center bg-white/10 rounded-lg flex-shrink-0">
                <i className={`${getFileIcon(preview.doc.file_type)} text-base`}></i>
              </div>
              <div className="min-w-0">
                <p className="text-white font-bold text-sm truncate">{preview.doc.file_name}</p>
                <p className="text-white/40 text-xs">{formatFileSize(preview.doc.file_size)}</p>
              </div>
            </div>
            <div className="flex items-center gap-3 flex-shrink-0">
              <button
                onClick={() => handleDownload(preview.doc)}
                className="flex items-center gap-2 bg-white/10 hover:bg-white/20 text-white font-bold text-sm px-4 py-2 rounded-lg transition-all whitespace-nowrap cursor-pointer"
              >
                <i className="ri-download-2-line"></i>Download
              </button>
              <button
                onClick={() => setPreview(null)}
                className="w-9 h-9 flex items-center justify-center bg-red-500/20 hover:bg-red-500/40 border border-red-500/40 rounded-lg transition-all cursor-pointer"
              >
                <i className="ri-close-line text-red-400 text-lg"></i>
              </button>
            </div>
          </div>
          <div className="flex-1 overflow-hidden flex items-center justify-center p-4">
            {preview.doc.file_type === 'application/pdf' ? (
              <iframe src={preview.url} title={preview.doc.file_name} className="w-full h-full rounded-lg border border-white/10" style={{ minHeight: '70vh' }} />
            ) : (
              <div className="max-w-full max-h-full flex items-center justify-center">
                <img src={preview.url} alt={preview.doc.file_name} className="max-w-full max-h-[80vh] object-contain rounded-lg" />
              </div>
            )}
          </div>
        </div>
      )}

      <div className="bg-white/5 backdrop-blur-md border border-white/10 rounded-2xl p-8">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h3 className="text-2xl font-black text-white">Documents from Attorney</h3>
            <p className="text-white/50 text-sm mt-1">Files your attorney has shared with you</p>
          </div>
          {unreadCount > 0 && (
            <span className="bg-[#FDB913] text-black text-xs font-black px-3 py-1.5 rounded-full whitespace-nowrap">
              {unreadCount} new
            </span>
          )}
        </div>

        <div className="space-y-3">
          {docs.map((doc) => (
            <div
              key={doc.id}
              className={`flex items-center gap-3 border rounded-xl p-4 transition-all ${
                !doc.is_viewed
                  ? 'bg-[#FDB913]/5 border-[#FDB913]/30'
                  : 'bg-white/5 border-white/10 hover:bg-white/10'
              }`}
            >
              <div className="w-10 h-10 flex items-center justify-center bg-white/10 rounded-lg flex-shrink-0">
                <i className={`${getFileIcon(doc.file_type)} text-lg`}></i>
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <p className="text-white text-sm font-bold truncate">{doc.file_name}</p>
                  {!doc.is_viewed && (
                    <span className="bg-[#FDB913] text-black text-[10px] font-black px-2 py-0.5 rounded-full whitespace-nowrap flex-shrink-0">
                      NEW
                    </span>
                  )}
                </div>
                <p className="text-white/40 text-xs">
                  {formatFileSize(doc.file_size)} &bull; {new Date(doc.uploaded_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                </p>
                {doc.description && (
                  <p className="text-white/60 text-xs mt-0.5 italic">{doc.description}</p>
                )}
              </div>
              <div className="flex items-center gap-2 flex-shrink-0">
                {isPreviewable(doc.file_type) && (
                  <button
                    onClick={() => handlePreview(doc)}
                    disabled={previewLoading === doc.id}
                    className="w-9 h-9 flex items-center justify-center bg-white/10 hover:bg-[#FDB913]/20 border border-white/20 hover:border-[#FDB913]/40 rounded-lg transition-all cursor-pointer disabled:opacity-50"
                    title="Preview"
                  >
                    {previewLoading === doc.id
                      ? <i className="ri-loader-4-line animate-spin text-white/60 text-base"></i>
                      : <i className="ri-eye-line text-white/60 text-base"></i>
                    }
                  </button>
                )}
                <button
                  onClick={() => handleDownload(doc)}
                  disabled={downloadingId === doc.id}
                  className="w-9 h-9 flex items-center justify-center bg-[#FDB913]/10 hover:bg-[#FDB913]/20 border border-[#FDB913]/30 rounded-lg transition-all cursor-pointer flex-shrink-0 disabled:opacity-50"
                  title="Download"
                >
                  {downloadingId === doc.id
                    ? <i className="ri-loader-4-line animate-spin text-[#FDB913] text-base"></i>
                    : <i className="ri-download-2-line text-[#FDB913] text-base"></i>
                  }
                </button>
              </div>
            </div>
          ))}
        </div>
      </div>
    </>
  );
}
